<script setup lang="ts">
/**
 * Узел интерактивного иерархического дерева видов нежелательных событий.
 *
 * Функционал:
 * - Отображение карточки категории вида НС (название, описание, последствия, активность);
 * - Поддержка Drag-and-Drop для изменения иерархии категорий в дереве;
 * - Отображение основного и дополнительных ответственных координаторов с их отделениями;
 * - Подсветка поисковых совпадений в тексте;
 * - Режимы отображения: стандартный и компактный (isCompact);
 * - Сворачивание / разворачивание дочерних поддеревьев (collapsedNodes).
 */

// Импорты Vue
import type { Ref } from 'vue'
import { computed, inject, ref, watch } from 'vue'

// Импорты типов и утилит
import type { IAdverseEventTypeTreeNode } from '@/modules/adverseEvents/types/IAdverseSettings'
import { getRandomAvatar } from '@/utils/getRandomAvatar'

/**
 * Входящие параметры узла дерева видов НС
 */
const props = withDefaults(defineProps<{
  /** Объект данных текущего узла дерева */
  node: IAdverseEventTypeTreeNode
  /** Идентификатор текущего выбранного узла */
  selectedNodeId: string | null
  /** Список ID свернутых узлов дерева */
  collapsedNodes: Array<string>
  /** Уровень вложенности узла (глубина в иерархии) */
  depth?: number
}>(), {
  depth: 0,
})

/**
 * События, генерируемые узлом дерева
 */
const emit = defineEmits<{
  /** Выбор узла для редактирования в сайдбаре */
  'select': [node: IAdverseEventTypeTreeNode]
  /** Переключение сворачивания дочерних элементов узла */
  'toggle-collapse': [id: string]
}>()

// ==========================================
// Внедренные зависимости (Injects из дерева)
// ==========================================

/** Глобальный флаг компактного режима */
const isCompactModeActive = inject<Ref<boolean>>('isCompactModeActive', ref(false))

/** Флаг включения перетаскивания (Drag & Drop) */
const isDragDropEnabled = inject<Ref<boolean>>('isDragDropEnabled', ref(false))

/** Переключение активности узла с карточки (для is_daomed_main) */
const onToggleNodeActive = inject<(node: IAdverseEventTypeTreeNode, isActive: boolean) => Promise<void> | void>(
  'onToggleNodeActive',
  () => {},
)

/** Локальный флаг компактного режима для конкретного узла */
const isLocallyCompact = ref(isCompactModeActive.value)

/** Текущая строка поискового запроса */
const searchQuery = inject<Ref<string>>('searchQuery', ref(''))

/** ID текущего перетаскиваемого узла */
const draggedNodeId = inject<Ref<string | null>>('draggedNodeId')

/** ID узла, над которым находится курсор при перетаскивании */
const dragOverNodeId = inject<Ref<string | null>>('dragOverNodeId')

/** Объект текущего перетаскиваемого узла */
const draggedNode = inject<Ref<IAdverseEventTypeTreeNode | null>>('draggedNode')

/** Обработчик старта перетаскивания */
const onNodeDragStart = inject<(node: IAdverseEventTypeTreeNode) => void>('onNodeDragStart')

/** Обработчик наведения при перетаскивании */
const onNodeDragOver = inject<(node: IAdverseEventTypeTreeNode) => void>('onNodeDragOver')

/** Обработчик ухода курсора при перетаскивании */
const onNodeDragLeave = inject<() => void>('onNodeDragLeave')

/** Обработчик сброса (drop) узла */
const onNodeDrop = inject<(targetNode: IAdverseEventTypeTreeNode) => void>('onNodeDrop')

/** Обработчик завершения перетаскивания */
const onNodeDragEnd = inject<() => void>('onNodeDragEnd')

// ==========================================
// Поиск и подсветка совпадений
// ==========================================

/**
 * Подсвечивает искомый фрагмент в тексте тегом <mark>
 *
 * @param text Исходный текст
 * @param searchTerm Искомая строка
 * @returns HTML-строка с разметкой подсветки
 */
const highlightMatch = (text: string, searchTerm: string) => {
  const query = (searchTerm || '').trim()
  if (!query || !text) {
    return text
  }
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${safe})`, 'gi')

  return text.replace(re, '<mark class="search-highlight">$1</mark>')
}

// Синхронизируем локальное состояние компактности с глобальным чекбоксом
watch(isCompactModeActive, newVal => {
  isLocallyCompact.value = newVal
})

/** Признак компактного отображения текущей карточки */
const isCompact = computed(() => {
  return isLocallyCompact.value
})

/**
 * Переключает режим компактности только для текущей карточки
 */
function toggleCompactOnly() {
  isLocallyCompact.value = !isLocallyCompact.value
}

/** Вычисленная глубина вложенности */
const currentDepth = computed(() => {
  const d = Number(props.depth)
  return isNaN(d) ? 0 : d
})

/** Название перетаскиваемого узла для бейджа-подсказки */
const draggedNodeName = computed(() => draggedNode?.value?.name || '')

/** Является ли текущий узел целью для drop */
const isCurrentDragOver = computed(() => {
  return dragOverNodeId ? dragOverNodeId.value === props.node.id : false
})

/** Является ли текущий узел источником перетаскивания */
const isCurrentDragged = computed(() => {
  return draggedNodeId ? draggedNodeId.value === props.node.id : false
})

/** Есть ли активный перетаскиваемый узел в дереве */
const hasDraggedNode = computed(() => {
  return draggedNodeId ? draggedNodeId.value !== null : false
})

/**
 * Глобальные карточки всегда визуально приглушены:
 * переносить нельзя, правится только активность.
 */
const isVisuallyLocked = computed(() => !!props.node.is_daomed_main)

const isTogglingActive = ref(false)

async function handleToggleActive(value: boolean | null) {
  if (isTogglingActive.value) return
  isTogglingActive.value = true
  try {
    await onToggleNodeActive(props.node, Boolean(value))
  } finally {
    isTogglingActive.value = false
  }
}

function onActivityChipClick() {
  if (isTogglingActive.value) return
  void handleToggleActive(!(props.node.is_active !== false))
}

/**
 * Обработка начала перетаскивания узла
 *
 * @param e Событие DragEvent
 * @param node Данные перетаскиваемого узла
 */
function handleDragStart(e: DragEvent, node: IAdverseEventTypeTreeNode) {
  if (!isDragDropEnabled.value || node.is_daomed_main) return
  onNodeDragStart?.(node)

  const target = e.currentTarget as HTMLElement
  if (target && e.dataTransfer) {
    try {
      // Временно переключаем карточку в состояние фантома переноса
      target.classList.add('is-drag-active-ghost')

      // Захватываем снимок измененного элемента
      e.dataTransfer?.setDragImage(target, 140, 32)

      // Синхронно/асинхронно в следующем тике возвращаем карточке обычный вид
      setTimeout(() => {
        target.classList.remove('is-drag-active-ghost')
      }, 0)
    } catch (err) {
      console.error('[NODE] Error setting drag image:', err)
      // В случае ошибки гарантированно очищаем класс
      target.classList.remove('is-drag-active-ghost')
    }
  }
}

/** Наличие дочерних элементов */
const hasChildren = computed(() => {
  return props.node.children && props.node.children.length > 0
})

/** Свернут ли текущий узел */
const isCollapsed = computed(() => {
  return props.collapsedNodes.includes(props.node.id)
})

/** Основной координатор вида НС (по умолчанию) */
const primaryResponsible = computed(() => {
  if (props.node.responsible_employees) {
    const found = props.node.responsible_employees.find(r => r.is_default === true)
    return found ? found.employee : null
  }
  return null
})

/** Список дополнительных координаторов с привязанными отделениями */
const otherResponsibles = computed(() => {
  if (props.node.responsible_employees) {
    return props.node.responsible_employees.filter(r => r.is_default !== true)
  }
  return []
})

const departmentLabel = (dept: { short_name?: string; name: string }) =>
  dept.short_name?.trim() || dept.name

/**
 * Получить текстовую метку уровня последствий
 *
 * @param consequence Числовой код последствий (0..4)
 * @returns Строковое название уровня последствий
 */
function getConsequenceLabel(consequence: number | null | undefined): string {
  if (consequence === null || consequence === undefined) return ''
  const consequenceLabels: Record<number, string> = {
    0: 'Несущественные',
    1: 'Легкие',
    2: 'Средние',
    3: 'Значительные',
    4: 'Катастрофические',
  }
  return consequenceLabels[consequence] || 'Неизвестно'
}

/**
 * Получить цвет бейджа уровня последствий
 *
 * @param consequence Числовой код последствий (0..4)
 * @returns Hex-код цвета
 */
function getConsequenceColor(consequence: number): string {
  const consequenceColors: Record<number, string> = {
    0: '#8A8D93', // grey
    1: '#56CA00', // green
    2: '#FFB400', // amber
    3: '#FF7C02', // orange
    4: '#FF4C51', // red
  }
  return consequenceColors[consequence] || '#8A8D93'
}

/** Кэш сгенерированных аватаров для сотрудников без фото */
const fallbackAvatars = new Map<string, string>()

/**
 * Получить URL аватара сотрудника
 *
 * @param emp Объект сотрудника
 * @returns Ссылка на аватар или сгенерированный плейсхолдер
 */
function getAvatarUrl(emp: any): string {
  if (!emp) return getRandomAvatar()
  if (emp.avatar) return emp.avatar
  const id = String(emp.id || 'default')
  if (!fallbackAvatars.has(id))
    fallbackAvatars.set(id, getRandomAvatar())
  return fallbackAvatars.get(id)!
}
</script>

<template>
  <li
    class="tree-node-li"
    :class="{
      'has-subdepartments': hasChildren && !isCollapsed,
      'is-daomed-locked-branch': isVisuallyLocked,
    }"
    :style="{ '--depth': currentDepth }"
  >
    <div
      class="tree-node-wrapper"
      :class="{ 'has-children': (hasChildren && !isCollapsed) || (isCurrentDragOver && hasDraggedNode) }"
    >
      <div
        :id="`dept-card-${node.id}`"
        class="dept-node-card"
        :class="{
          'selected': selectedNodeId === String(node.id),
          'mo-card': node.isMo,
          'is-dragging-origin': isCurrentDragged,
          'is-dragging-over': isCurrentDragged && dragOverNodeId !== null,
          'drag-over': isCurrentDragOver,
          'is-dragging-active': hasDraggedNode,
          'compact': isCompact,
          'is-daomed-locked': isVisuallyLocked,
        }"
        :draggable="isDragDropEnabled && !node.is_daomed_main"
        @dragstart="handleDragStart($event, node)"
        @dragover.prevent="onNodeDragOver && onNodeDragOver(node)"
        @dragleave="onNodeDragLeave && onNodeDragLeave()"
        @drop="onNodeDrop && onNodeDrop(node)"
        @dragend="onNodeDragEnd && onNodeDragEnd()"
      >
        <!-- Блок фантома переноса, который активируется по классу .is-drag-active-ghost -->
        <div class="custom-drag-ghost" style="display: none;">
          <div style="display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 6px; background: rgba(255, 255, 255, 0.12); flex-shrink: 0;">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20" />
            </svg>
          </div>
          <div style="display: flex; flex-direction: column; overflow: hidden; flex-grow: 1; text-align: left;">
            <span style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: rgba(255, 255, 255, 0.45); margin-bottom: 2px;">Перенос подкатегории</span>
            <span style="font-size: 13px; font-weight: 600; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
              {{ node.name }}
            </span>
          </div>
        </div>

        <div
          class="d-flex flex-column px-4 py-3 border-b dept-header w-100"
          @click.stop="emit('select', node)"
        >
          <!-- Верхняя строка: Бейджи + Стрелочка -->
          <div class="d-flex align-center justify-space-between w-100 gap-2">
            <div class="d-flex flex-wrap align-center gap-1 min-width-0">
              <VChip
                v-if="node.is_daomed_main"
                size="x-small"
                color="primary"
                variant="tonal"
                class="font-weight-medium px-2 card-status-chip"
              >
                Глобальный
              </VChip>
              <VChip
                v-if="node.is_active !== undefined"
                size="x-small"
                :color="node.is_active !== false ? 'success' : 'secondary'"
                variant="tonal"
                class="font-weight-medium px-2 card-status-chip activity-chip cursor-pointer"
                @click.stop="onActivityChipClick"
              >
                <span>{{ node.is_active !== false ? 'Активен' : 'Неактивен' }}</span>
                <VSwitch
                  :model-value="node.is_active !== false"
                  color="success"
                  hide-details
                  density="compact"
                  inset
                  class="activity-chip-switch"
                  tabindex="-1"
                />
              </VChip>
            </div>
            <VIcon
              :icon="isCompact ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'"
              size="20"
              class="cursor-pointer text-medium-emphasis hover-primary flex-shrink-0"
              @click.stop="toggleCompactOnly"
            />
          </div>

          <!-- Нижняя строка: Название (со скроллом при переполнении) -->
          <div class="dept-title-scroll flex-grow-1 min-width-0">
            <span
              class="font-weight-bold text-high-emphasis"
              style="font-size: 14px; line-height: 1.3;"
              v-html="highlightMatch(node.name, searchQuery)"
            />
          </div>
        </div>

        <template v-if="!isCompact">
          <!-- Основной ответственный (Координатор по умолчанию) -->
          <div
            v-if="primaryResponsible"
            class="d-flex align-center gap-3 px-4 py-3 border-b dept-manager cursor-pointer-hover"
            @click="emit('select', node)"
          >
            <VAvatar size="42" class="border" :image="getAvatarUrl(primaryResponsible)" />
            <div class="flex-grow-1 min-width-0">
              <div
                class="text-body-1 font-weight-bold text-truncate text-high-emphasis manager-name-text"
                v-html="highlightMatch(`${primaryResponsible.first_name} ${primaryResponsible.last_name}`, searchQuery)"
              />
              <div class="text-caption text-medium-emphasis">
                {{ primaryResponsible.position?.name || 'Координатор по умолчанию' }}
              </div>
            </div>
          </div>
          <div
            v-else
            class="d-flex align-center gap-3 px-4 py-3 border-b dept-manager text-medium-emphasis"
            @click="emit('select', node)"
          >
            <VAvatar size="42" class="border d-flex align-center justify-center" style="background-color: rgba(var(--v-theme-on-surface), 0.04) !important;">
              <VIcon icon="ri-user-line" size="20" class="text-medium-emphasis" />
            </VAvatar>
            <div class="flex-grow-1 min-width-0">
              <div class="text-body-2 font-weight-medium text-high-emphasis">
                Координатор по умолчанию
              </div>
              <div class="text-caption text-medium-emphasis font-weight-medium">
                Не назначен
              </div>
            </div>
          </div>

          <!-- Детали: Последствия и Координаторы -->
          <div
            class="d-flex flex-column px-4 py-3 gap-2 dept-details"
            @click="emit('select', node)"
          >
            <!-- Последствия -->
            <div class="d-flex align-center justify-space-between">
              <span class="text-caption text-medium-emphasis font-weight-medium">Последствия:</span>
              <span
                v-if="node.consequence !== null && node.consequence !== undefined"
                class="text-body-2 font-weight-medium text-high-emphasis"
                v-html="highlightMatch(getConsequenceLabel(node.consequence), searchQuery)"
              />
              <span v-else class="text-body-2 text-high-emphasis font-weight-medium">
                Не указаны
              </span>
            </div>

            <!-- Координаторы -->
            <div class="d-flex flex-column gap-2 min-width-0">
              <div class="d-flex align-center justify-space-between">
                <span class="text-caption text-medium-emphasis font-weight-medium">Координаторы:</span>
                <span v-if="!otherResponsibles.length" class="text-body-2 text-high-emphasis font-weight-medium">
                  Нет
                </span>
              </div>
              <div
                v-if="otherResponsibles.length"
                class="d-flex flex-column gap-2 min-width-0"
              >
                <div
                  v-for="resp in otherResponsibles"
                  :key="resp.id"
                  class="d-flex align-start ga-2 min-width-0"
                >
                  <VAvatar size="22" class="border flex-shrink-0" :image="getAvatarUrl(resp.employee)" />
                  <div class="d-flex flex-column min-width-0 flex-grow-1">
                    <span
                      class="coordinator-line text-body-2 font-weight-medium text-high-emphasis"
                      v-html="highlightMatch(`${resp.employee.first_name || ''} ${resp.employee.last_name || ''}`, searchQuery)"
                    />
                    <div
                      v-if="resp.departments?.length"
                      class="coordinator-line text-caption text-medium-emphasis"
                    >
                      <div
                        v-for="dept in resp.departments"
                        :key="dept.id"
                      >
                        {{ departmentLabel(dept) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Нижняя кнопка-плашка для раскрытия детей / drop zone -->
        <div
          v-if="isCurrentDragOver"
          class="bottom-action-btn drag-over-active d-flex align-center justify-center py-2 text-primary w-100"
          style="background-color: rgba(var(--v-theme-primary), 0.15) !important;"
        >
          <VIcon icon="ri-arrow-down-double-line" size="16" class="mr-1 text-primary animate-bounce-down" />
          <span class="text-caption font-weight-bold text-primary">
            Сделать подкатегорией
          </span>
        </div>
        <div
          v-else-if="hasChildren"
          class="bottom-action-btn d-flex align-center justify-center py-2"
          :class="{ expanded: !isCollapsed }"
          @click.stop="$emit('toggle-collapse', node.id)"
        >
          <span class="text-caption font-weight-bold mr-1">{{ node.children?.length || 0 }} подкатегорий</span>
          <VIcon :icon="isCollapsed ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'" size="16" />
        </div>
        <div
          v-else
          class="bottom-action-btn disabled d-flex align-center justify-center py-2 text-medium-emphasis"
        >
          <span class="text-caption font-weight-medium">Нет подкатегорий</span>
        </div>
      </div>
    </div>

    <!-- Рекурсивный рендеринг дочерних узлов -->
    <ul v-if="(hasChildren && !isCollapsed) || (dragOverNodeId === node.id && draggedNodeId !== null)">
      <!-- Рендерим существующих детей -->
      <template v-if="hasChildren && !isCollapsed">
        <AdverseEventTypeTreeNode
          v-for="sub in node.children"
          :key="sub.id"
          :node="sub"
          :selected-node-id="selectedNodeId"
          :collapsed-nodes="collapsedNodes"
          :depth="currentDepth + 1"
          @select="n => $emit('select', n)"
          @toggle-collapse="id => $emit('toggle-collapse', id)"
        />
      </template>

      <!-- Временное превью перетаскиваемой карточки как дочерней -->
      <li v-if="dragOverNodeId === node.id && draggedNodeId !== null" class="preview-node-li" :style="{ '--depth': currentDepth + 1 }">
        <div class="tree-node-wrapper">
          <div class="dept-node-card preview-card">
            <div class="d-flex align-center gap-2 px-4 py-2 border-b w-100 justify-center" style="background: rgba(var(--v-theme-primary), 0.04); border-color: rgba(var(--v-theme-primary), 0.12) !important;">
              <VIcon icon="ri-git-branch-line" size="16" class="text-primary" />
              <span class="font-weight-bold text-truncate text-primary" style="font-size: 13px; max-width: 240px;">
                {{ draggedNodeName }}
              </span>
            </div>
            <div class="d-flex align-center justify-center flex-grow-1 w-100 py-2">
              <span class="text-caption text-medium-emphasis font-weight-bold d-flex align-center gap-1">
                <VIcon icon="ri-arrow-down-double-line" size="14" class="text-primary animate-bounce-down" />
                Станет подкатегорией
              </span>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </li>
</template>

<style scoped lang="scss">
.dept-details {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  font-size: 12px;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--v-theme-on-surface), 0.2);
    border-radius: 4px;
  }
}

.coordinator-line {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.risk-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.preview-node-li {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-top: 32px;
  list-style-type: none;
  flex-shrink: 0;
  width: min-content;
}

.preview-card {
  width: 320px !important;
  height: 120px !important;
  border: 2px dashed rgb(var(--v-theme-primary)) !important;
  background-color: rgba(var(--v-theme-primary), 0.04) !important;
  box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.15) !important;
  opacity: 0.85;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: scale(0.95);
  animation: pulsePreview 1.8s infinite ease-in-out;
}

@keyframes pulsePreview {
  0%, 100% {
    opacity: 0.7;
    transform: scale(0.93) translateY(0);
    box-shadow: 0 4px 10px rgba(var(--v-theme-primary), 0.1);
  }
  50% {
    opacity: 0.95;
    transform: scale(0.97) translateY(-2px);
    box-shadow: 0 8px 20px rgba(var(--v-theme-primary), 0.25);
  }
}

.custom-drag-ghost {
  display: none;
}

.dept-node-card.is-drag-active-ghost {
  width: 280px !important;
  height: 64px !important;
  min-height: 64px !important;
  max-height: 64px !important;
  background: #2f273e !important;
  border: 1.5px solid rgb(138, 92, 246) !important;
  border-radius: 8px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
  color: #ffffff !important;
  font-family: 'Inter', sans-serif !important;
  box-sizing: border-box !important;

  & > :not(.custom-drag-ghost) {
    display: none !important;
  }

  .custom-drag-ghost {
    display: flex !important;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 100%;
    padding: 10px 14px;
    box-sizing: border-box;
  }
}

.dept-node-card.is-dragging-active * {
  pointer-events: none !important;
}

.dept-node-card.is-daomed-locked {
  opacity: 0.82;
}

.dept-node-card.is-daomed-locked:hover {
  opacity: 0.92;
}

.card-status-chip {
  height: 22px !important;
  min-height: 22px !important;
}

.activity-chip {
  padding-inline: 6px 4px !important;
  overflow: hidden;

  :deep(.v-chip__content) {
    height: 22px;
    max-height: 22px;
    gap: 2px;
    overflow: hidden;
  }
}

.activity-chip-switch {
  margin-inline: 0 !important;
  flex: 0 0 auto;
  width: 26px;
  height: 16px;
  overflow: hidden;
  transform: scale(0.65);
  transform-origin: center center;
  pointer-events: none;

  :deep(.v-input),
  :deep(.v-input__control),
  :deep(.v-selection-control) {
    min-height: 0 !important;
    height: 16px;
    width: 28px;
  }

  :deep(.v-selection-control__wrapper) {
    height: 14px;
    width: 26px;
  }

  :deep(.v-switch__track) {
    opacity: 1;
    height: 12px !important;
    width: 24px !important;
    min-width: 24px !important;
  }

  :deep(.v-switch__thumb) {
    height: 8px !important;
    width: 8px !important;
  }
}

.hover-primary {
  transition: color 0.15s ease-in-out;
}
.hover-primary:hover {
  color: rgb(var(--v-theme-primary)) !important;
}

:deep(.search-highlight) {
  background-color: rgb(var(--v-theme-primary-500), var(--v-activated-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  padding: 2px 0;
  border-radius: 2px;
}

:deep(.v-theme--dark .search-highlight) {
  background-color: rgba(var(--v-theme-primary-500), 0.38) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  padding: 2px 0;
  border-radius: 2px;
}
</style>
